import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'
import { useOnboarding } from '../hooks/useOnboarding'
import {
  PageLoading,
  ErrorMessage,
  Card,
  StatCard,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  formatEuro,
  Button,
} from '../components'

// Issue #536: Enhanced dashboard data structure
interface DashboardData {
  fiscalPeriod: {
    id: string
    name: string
    year: number
    startDate: string
    endDate: string
    status: string
  } | null
  // 1. Kassatilanne
  bankBalance: {
    total: number
    bankAccounts: number
  }
  // 2. Tilikauden tulos
  periodResult: {
    income: number
    expenses: number
    netResult: number
    isProfit: boolean
  }
  // 3. Viimeisimmät kirjaukset with receipt links
  recentEntries: Array<{
    id: string
    date: string
    description: string
    amount: number
    voucherNumber: number | null
    status: string
    receipt: {
      fileId: string
      filename: string
      contentType: string
      downloadUrl: string
    } | null
  }>
  // 4. Hyväksyntäjono
  pendingApprovals: {
    queueCount: number
    pendingReceipts: number
    totalPending: number
  }
  // 5. Budjettivertailu
  budgetComparison: Array<{
    category: string
    budgeted: number
    spent: number
    remaining: number
    percentageUsed: number
  }>
  // 6. Budjettivaroitukset (Issue #583)
  budgetAlerts: {
    recentAlerts: Array<{
      id: string
      alertType: 'approaching' | 'exceeded'
      category: string
      actualPercent: number
      thresholdPercent: number
      message: string
      createdAt: string
    }>
    categoryAlerts: Array<{
      id: string
      category: string
      alertLevel: 'warning' | 'critical'
      spentPercent: number
      thresholdPercent: number
      limitAmount: number
      spentAmount: number
    }>
    totalActive: number
  }
  summary: {
    totalJournalEntries: number
  }
}

interface OverdueSummary {
  count: number
  total_amount: number
}

export default function DashboardPage() {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const navigate = useNavigate()
  const { progress: onboardingProgress, isComplete: onboardingComplete } = useOnboarding()
  const [data, setData] = useState<DashboardData | null>(null)
  const [overdueData, setOverdueData] = useState<OverdueSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!activeOrg) return
    setLoading(true)

    // Fetch new dashboard data (Issue #536)
    apiClient.get<{ success: boolean; data: DashboardData }>(`/dashboard?organization_id=${activeOrg.id}`)
      .then((response) => { 
        setData(response.data); 
        setLoading(false) 
      })
      .catch(() => { setError(t('dashboard.errors.loadFailed')); setLoading(false) })

    // Fetch overdue invoices summary
    apiClient.get<{ invoices: Array<{ totalAmount: number }> }>(`/invoices?organization_id=${activeOrg.id}&status=overdue`)
      .then((d) => {
        const invoices = d.invoices || []
        const totalAmount = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0)
        setOverdueData({ count: invoices.length, total_amount: totalAmount })
      })
      .catch(() => setOverdueData(null))
  }, [activeOrg, t])

  if (!activeOrg) {
    return <NoOrganizationMessage />
  }

  if (loading) return <PageLoading />
  if (error) return <ErrorMessage message={error} />
  if (!data) return null

  // Stats for the top cards
  const stats = [
    {
      label: t('dashboard.stats.totalEntries'),
      value: data.summary.totalJournalEntries,
      icon: '📊',
    },
    {
      label: t('dashboard.stats.bankBalance'),
      value: formatEuro(data.bankBalance.total),
      icon: '🏦',
      isCurrency: true,
    },
    {
      label: t('dashboard.stats.pendingApproval'),
      value: data.pendingApprovals.totalPending,
      icon: '⏳',
      warning: data.pendingApprovals.totalPending > 0,
    },
    {
      label: data.periodResult?.isProfit ? t('dashboard.stats.profit') : t('dashboard.stats.loss'),
      value: formatEuro(Math.abs(data.periodResult?.netResult || 0)),
      icon: data.periodResult?.isProfit ? '📈' : '📉',
      isCurrency: true,
      positive: data.periodResult?.isProfit,
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <PageHeader 
        title={`${t('dashboard.title')} — ${activeOrg.name}`}
        subtitle={data.fiscalPeriod ? `${data.fiscalPeriod.name} (${data.fiscalPeriod.startDate} — ${data.fiscalPeriod.endDate})` : undefined}
      />

      {/* Onboarding banner */}
      {onboardingProgress && !onboardingComplete && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🚀</span>
            <div>
              <div className="font-medium text-blue-900">Aloitusprosessi kesken</div>
              <div className="text-sm text-blue-600">
                {onboardingProgress.progress_percent}% valmis — jatka aloitusta saadaksesi kaiken irti Kirstunvartijasta
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 whitespace-nowrap"
          >
            Jatka aloitusta →
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon, warning, positive }) => (
          <StatCard
            key={label}
            title={label}
            value={value}
            icon={icon}
            warning={warning}
            positive={positive}
          />
        ))}
      </div>

      {/* Financial summary - Tilikauden tulos */}
      <Card 
        title={t('dashboard.finance.periodResult')} 
        subtitle={data.fiscalPeriod?.name || String(new Date().getFullYear())}
      >
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-500">{t('dashboard.finance.income')}</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {formatEuro(data.periodResult.income)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">{t('dashboard.finance.expenses')}</div>
            <div className="text-2xl font-bold text-red-500 mt-1">
              {formatEuro(data.periodResult.expenses)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">{t('dashboard.finance.net')}</div>
            <div className={`text-2xl font-bold mt-1 ${data.periodResult.isProfit ? 'text-green-600' : 'text-red-500'}`}>
              {data.periodResult.isProfit ? '+' : '-'}{formatEuro(Math.abs(data.periodResult.netResult))}
            </div>
          </div>
        </div>
      </Card>

      {/* Bank Balance - Kassatilanne */}
      <Card title={t('dashboard.bankBalance.title')}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500">{t('dashboard.bankBalance.total')}</div>
            <div className={`text-3xl font-bold mt-1 ${data.bankBalance.total >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {formatEuro(data.bankBalance.total)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">{t('dashboard.bankBalance.accounts')}</div>
            <div className="text-2xl font-semibold text-gray-700">{data.bankBalance.bankAccounts}</div>
          </div>
        </div>
      </Card>

      {/* Pending Approvals - Hyväksyntäjono */}
      {data.pendingApprovals.totalPending > 0 && (
        <Card 
          title={t('dashboard.pendingApprovals.title')}
          className="border-amber-200 bg-amber-50"
        >
          <div className="flex items-center justify-between">
            <div className="flex gap-8">
              <div>
                <div className="text-sm text-amber-700">{t('dashboard.pendingApprovals.queue')}</div>
                <div className="text-2xl font-bold text-amber-800">
                  {data.pendingApprovals.queueCount}
                </div>
              </div>
              <div>
                <div className="text-sm text-amber-700">{t('dashboard.pendingApprovals.receipts')}</div>
                <div className="text-2xl font-bold text-amber-800">
                  {data.pendingApprovals.pendingReceipts}
                </div>
              </div>
              <div>
                <div className="text-sm text-amber-700">{t('dashboard.pendingApprovals.total')}</div>
                <div className="text-2xl font-bold text-amber-800">
                  {data.pendingApprovals.totalPending}
                </div>
              </div>
            </div>
            <Link to="/approval-queue">
              <Button variant="primary">{t('dashboard.pendingApprovals.action')}</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Budget Comparison - Budjettivertailu */}
      {data.budgetComparison.length > 0 && (
        <Card title={t('dashboard.budgetComparison.title')}>
          <div className="space-y-4">
            {data.budgetComparison.map((budget) => (
              <div key={budget.category} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">{budget.category}</span>
                  <span className={`text-sm font-semibold ${
                    budget.percentageUsed > 100 ? 'text-red-600' :
                    budget.percentageUsed > 80 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {budget.percentageUsed}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${
                      budget.percentageUsed > 100 ? 'bg-red-600' :
                      budget.percentageUsed > 80 ? 'bg-amber-500' : 'bg-green-600'
                    }`}
                    style={{ width: `${Math.min(budget.percentageUsed, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{t('dashboard.budgetComparison.spent')}: {formatEuro(budget.spent)}</span>
                  <span>{t('dashboard.budgetComparison.budgeted')}: {formatEuro(budget.budgeted)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-right">
            <Link to="/budgets">
              <Button variant="secondary" size="sm">{t('dashboard.budgetComparison.viewAll')}</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Budget Alerts Widget - Issue #583 */}
      {data.budgetAlerts?.totalActive > 0 && (
        <Card
          title="⚠️ Budjettivaroitukset"
          className="border-amber-200"
        >
          <div className="space-y-3">
            {data.budgetAlerts.categoryAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-3 rounded-lg border ${
                  alert.alertLevel === 'critical'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {alert.alertLevel === 'critical' ? '🚨' : '⚠️'}
                    </span>
                    <span className="font-medium text-gray-800">
                      {alert.category}
                    </span>
                  </div>
                  <Badge
                    variant={alert.alertLevel === 'critical' ? 'error' : 'warning'}
                    size="sm"
                  >
                    {alert.spentPercent}%
                  </Badge>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {alert.alertLevel === 'critical'
                    ? `Budjetti ylitetty! Käytetty ${formatEuro(alert.spentAmount)} / ${formatEuro(alert.limitAmount)}`
                    : `Budjetti lähestymässä rajaa. Raja: ${alert.thresholdPercent}%`
                  }
                </div>
              </div>
            ))}
            {data.budgetAlerts.recentAlerts.slice(0, 3).map((alert) => (
              <div
                key={alert.id}
                className="p-3 rounded-lg border bg-blue-50 border-blue-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {alert.alertType === 'exceeded' ? '🚨' : 'ℹ️'}
                    </span>
                    <span className="font-medium text-gray-800">
                      {alert.category || 'Budjetti'}
                    </span>
                  </div>
                  <Badge
                    variant={alert.alertType === 'exceeded' ? 'error' : 'info'}
                    size="sm"
                  >
                    {alert.actualPercent}%
                  </Badge>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {alert.message}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Link to="/category-budget-limits">
              <Button variant="secondary" size="sm">
                Hallitse varoituksia
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Overdue Invoices Widget */}
      {overdueData && overdueData.count > 0 && (
        <Card
          title="⚠️ Erääntyneet laskut"
          className="border-red-200 bg-red-50"
        >
          <div className="flex items-center justify-between">
            <div className="flex gap-8">
              <div>
                <div className="text-sm text-red-600">Lukumäärä</div>
                <div className="text-2xl font-bold text-red-700">
                  {overdueData.count}
                </div>
              </div>
              <div>
                <div className="text-sm text-red-600">Yhteissumma</div>
                <div className="text-2xl font-bold text-red-700">
                  {formatEuro(overdueData.total_amount)}
                </div>
              </div>
            </div>
            <Link to="/invoices/overdue">
              <Button variant="primary">Näytä kaikki</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Recent Entries - Viimeisimmät kirjaukset */}
      <Card title={t('dashboard.recentEntries.title')}>
        {data.recentEntries.length === 0 ? (
          <p className="text-gray-400">{t('common.noData')}</p>
        ) : (
          <div className="divide-y">
            {data.recentEntries.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{entry.description || '—'}</div>
                  <div className="text-sm text-gray-400">
                    {entry.date} 
                    {entry.voucherNumber && (
                      <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">
                        #{entry.voucherNumber}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {entry.receipt && (
                    <a 
                      href={entry.receipt.downloadUrl}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                      title={entry.receipt.filename}
                    >
                      📎
                    </a>
                  )}
                  <span className={`font-semibold ${entry.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatEuro(entry.amount)}
                  </span>
                  {entry.status === 'approved' ? (
                    <Badge variant="success" size="sm">✓</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">{t('dashboard.recentEntries.pending')}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 text-right">
          <Link to="/journal">
            <Button variant="secondary" size="sm">{t('dashboard.recentEntries.viewAll')}</Button>
          </Link>
        </div>
      </Card>
    </div>
  )
}
